/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { ResponsiveLine } from '@nivo/line';

import { Grid } from '@material-ui/core';

import moment from 'moment';

import 'moment/locale/es';
import 'moment/locale/ja';
import 'moment/locale/fr';
import 'moment/locale/de';
import 'moment/locale/nl';
import 'moment/locale/pt';
import 'moment/locale/ar-dz';
import 'moment/locale/it';
import 'moment/locale/zh-cn';
import 'moment/locale/th';
import 'moment/locale/fa';
import 'moment/locale/ko';
import 'moment/locale/vi';
import 'moment/locale/id';

import { useStyles } from './styles';

import { ChartData, LineChartData } from './LineChartModel';

import { formatNumber, formatNumberForChart } from '../../../utils';

import { useAppData } from '../../../providers';

interface Props {
  currency?: string;
  data: ChartData[];
  colors: string[];
  valueType?: 'amount' | 'count';
}

export const LineChart: FC<Props> = ({ currency, data, colors, valueType = 'amount' }) => {
  const classes = useStyles();

  const { locale } = useAppData();

  const containerRef = useRef<HTMLDivElement>(null);

  const [chartThemeFontSize, setChartThemeFontSize] = useState(13);

  const [series, setSeries] = useState<any[]>([]);

  const [maxY, setMaxY] = useState<number | 'auto'>('auto');

  const [minY, setMinY] = useState<number | 'auto'>('auto');

  const [tickValues, setTickValues] = useState('every day');

  const chartTheme = useMemo(
    () => ({
      axis: {
        ticks: {
          text: {
            fontSize: chartThemeFontSize,
          },
        },
      },
    }),
    [chartThemeFontSize],
  );

  useEffect(() => {
    const formattedSeries: any[] = [];

    const allData: LineChartData[] = [];

    data.forEach((element) => {
      const chartData = Array.isArray(element.data) ? element.data : [];

      allData.push(...chartData);

      formattedSeries.push({
        id: element.id,
        data: chartData.map((reading) => ({
          x: reading.period,
          y: reading.amount,
        })),
      });
    });

    setSeries(formattedSeries);

    const yValues = allData.map((item) => item.amount);

    if (yValues.length === 0) {
      setMinY('auto');
      setMaxY('auto');
      return;
    }

    if (yValues.every((value) => value === 0)) {
      setMinY(0);
      setMaxY(valueType === 'count' ? 1 : 5);
      return;
    }

    const minValue = Math.min(...yValues);

    const maxValue = Math.max(...yValues);

    if (minValue < 0) {
      const absMax = Math.max(Math.abs(minValue), Math.abs(maxValue));

      let step = 500;

      if (absMax > 10000) {
        step = 5000;
      } else if (absMax > 5000) {
        step = 2500;
      } else if (absMax > 1000) {
        step = 1000;
      }

      setMinY(Math.floor(minValue / step) * step);
    } else {
      setMinY('auto');
    }

    setMaxY('auto');
  }, [data, valueType]);

  const getMomentLocale = () => {
    if (locale === 'ar') {
      return 'ar-dz';
    }

    if (locale === 'zh') {
      return 'zh-cn';
    }

    return locale || 'en';
  };

  const formatDate = (value: any) => {
    const date = moment(value);

    date.locale(getMomentLocale());

    return date.format('dddd DD, MMM YYYY');
  };

  const formatValue = (value: number | string) => {
    if (valueType === 'count') {
      return Math.round(Number(value)).toString();
    }

    return currency ? formatNumberForChart(Number(value), currency) : Number(value).toFixed(2);
  };

  const toolTipElement = (props: any) => (
    <div className={classes.toolTip} dir="ltr">
      <Grid container direction="column">
        <Grid item className={classes.amount}>
          {formatValue(props.point.data.y)}
        </Grid>

        <Grid item className={classes.period}>
          {formatDate(props.point.data.x)}
        </Grid>
      </Grid>
    </div>
  );

  useEffect(() => {
    const updateChartSettings = () => {
      const width = containerRef.current?.clientWidth || window.innerWidth;

      const maxLen = Math.max(...data.map((item) => item.data?.length || 0), 0);

      if (width < 400) {
        setChartThemeFontSize(8);
      } else if (width < 600) {
        setChartThemeFontSize(11);
      } else {
        setChartThemeFontSize(13);
      }

      if (width >= 900) {
        if (maxLen <= 20) {
          setTickValues('every day');
        } else if (maxLen <= 180) {
          setTickValues('every week');
        } else if (maxLen <= 730) {
          setTickValues('every month');
        } else if (maxLen <= 1500) {
          setTickValues('every 3 month');
        } else {
          setTickValues('every year');
        }

        return;
      }

      if (width >= 500) {
        if (maxLen <= 14) {
          setTickValues('every day');
        } else if (maxLen <= 120) {
          setTickValues('every week');
        } else if (maxLen <= 730) {
          setTickValues('every month');
        } else if (maxLen <= 1500) {
          setTickValues('every 3 month');
        } else {
          setTickValues('every year');
        }

        return;
      }

      if (maxLen <= 10) {
        setTickValues('every day');
      } else if (maxLen <= 60) {
        setTickValues('every week');
      } else if (maxLen <= 365) {
        setTickValues('every month');
      } else {
        setTickValues('every year');
      }
    };

    updateChartSettings();

    window.addEventListener('resize', updateChartSettings);

    return () => {
      window.removeEventListener('resize', updateChartSettings);
    };
  }, [data]);

  return (
    <div className={classes.chartRoot} dir="ltr" ref={containerRef}>
      <ResponsiveLine
        curve="linear"
        data={series}
        theme={chartTheme}
        colors={colors}
        enableGridY
        enableGridX={false}
        margin={{
          top: 14,
          right: 80,
          bottom: 60,
          left: 20,
        }}
        yScale={{
          type: 'linear',
          min: minY,
          max: maxY,
          stacked: false,
        }}
        xFormat="time:%Y-%m-%d"
        xScale={{
          format: '%Y-%m-%d',
          precision: 'day',
          type: 'time',
          useUTC: false,
        }}
        axisBottom={{
          tickSize: 0,
          tickPadding: 10,
          tickValues,
          format(value) {
            const date = moment(value);

            date.locale(getMomentLocale());

            return date.format('MMM DD');
          },
        }}
        axisLeft={null}
        axisRight={{
          tickSize: 0,
          tickPadding: 10,
          tickRotation: 0,
          tickValues: valueType === 'count' ? 2 : undefined,
          format: (value) => {
            if (valueType === 'count') {
              return Math.round(Number(value));
            }

            return currency ? formatNumber(Number(value), currency) : Number(value).toFixed(2);
          },
        }}
        enablePoints={false}
        useMesh
        tooltip={toolTipElement}
      />
    </div>
  );
};
